import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { ensureSocialLoginInitialized } from './socialLoginInit';

const webClientId = process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID || '';
const iosClientId = process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID || '';

export function canUseNativeGoogleSignIn() {
  if (!Capacitor.isNativePlatform()) return false;
  if (!webClientId) return false;
  if (Capacitor.getPlatform() === 'ios' && !iosClientId) return false;
  return true;
}

export async function ensureNativeGoogleInitialized() {
  if (!canUseNativeGoogleSignIn()) {
    throw new Error('Native Google Sign-In is not configured');
  }
  return ensureSocialLoginInitialized();
}

function randomNonce() {
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Native Google Sign-In via Capgo SocialLogin.
 * @returns {{ idToken: string, accessToken: string|null, nonce: string }}
 */
export async function signInWithNativeGoogleIdToken() {
  await ensureNativeGoogleInitialized();
  // Supabase hashes this raw nonce and compares to the Google id_token `nonce` claim.
  const nonce = randomNonce();
  const res = await SocialLogin.login({
    provider: 'google',
    options: {
      scopes: ['email', 'profile'],
      nonce,
    },
  });
  if (res.provider !== 'google') {
    throw new Error('Unexpected login provider');
  }
  const result = res.result;
  console.log('[nativeGoogleAuth] SocialLogin google result', res);
  try {
    console.log('[nativeGoogleAuth] full result:', JSON.stringify(result, null, 2));
  } catch (e) {
    console.warn('[nativeGoogleAuth] full result: JSON.stringify failed', e);
  }

  const idToken =
    (result && typeof result === 'object' && (
      result.idToken ||
      result.id_token ||
      (result.authentication && (result.authentication.idToken || result.authentication.id_token))
    )) ||
    null;

  if (typeof idToken !== 'string' || idToken.length === 0) {
    const accessTokenShape =
      result && typeof result === 'object' && result.accessToken && typeof result.accessToken === 'object'
        ? Object.keys(result.accessToken)
        : [];
    console.error('[nativeGoogleAuth] Missing idToken in Google response', {
      responseType: result && typeof result === 'object' ? result.responseType : null,
      hasAccessToken: Boolean(result && result.accessToken),
      accessTokenShape,
    });
    throw new Error('Google did not return an id token');
  }

  let accessToken = null;
  if (result && typeof result === 'object') {
    if (typeof result.accessToken === 'string') {
      accessToken = result.accessToken;
    } else if (result.accessToken && typeof result.accessToken.token === 'string') {
      accessToken = result.accessToken.token;
    } else if (result.authentication && typeof result.authentication.accessToken === 'string') {
      accessToken = result.authentication.accessToken;
    }
  }

  return { idToken, accessToken, nonce };
}

export async function signOutNativeGoogle() {
  if (!canUseNativeGoogleSignIn()) return;
  try {
    await SocialLogin.logout({ provider: 'google' });
  } catch {
    /* ignore */
  }
}
